/**
 * MobileMenu — Slide-in drawer menu for mobile/tablet viewports.
 * Accordion-style expansion for nested menu items (up to 3 levels).
 */
import { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, Link } from 'react-router-dom'
import { Menu, X, ChevronDown, ExternalLink, Home, ShoppingBag, Phone } from 'lucide-react'
import { usePublicMenu, type MenuItem, type MenuTree } from '@/hooks/usePublicMenu'

// ─── Props ────────────────────────────────────────────────────────────────────

interface MobileMenuProps {
  /** Menu slug to fetch. Defaults to 'main-menu'. */
  slug?: string
  /** Optionally pass menu data directly (useful for testing). */
  menu?: MenuTree | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin !== window.location.origin
  } catch {
    return false
  }
}

function getItemHref(item: MenuItem): string {
  switch (item.link_type) {
    case 'category':
      return `/categories/${item.category_slug ?? item.link_target}/products`
    case 'custom_url':
      return item.link_target ?? '#'
    case 'internal_page':
      return item.link_target ?? '/'
    default:
      return '/'
  }
}

// ─── Accordion Item ───────────────────────────────────────────────────────────

interface AccordionItemProps {
  item: MenuItem
  depth: number
  expandedIds: Set<number>
  onToggle: (id: number) => void
  onNavigate: (item: MenuItem) => void
}

function AccordionItem({ item, depth, expandedIds, onToggle, onNavigate }: AccordionItemProps) {
  const hasChildren = item.children && item.children.length > 0
  const isExpanded = expandedIds.has(item.id)

  return (
    <div>
      <div
        className={`flex items-center justify-between ${depth === 0 ? 'border-b border-gray-100' : ''}`}
        style={{ paddingLeft: `${(depth + 1) * 1.25}rem` }}
      >
        {hasChildren ? (
          <>
            <button
              type="button"
              className={`flex-1 py-3.5 pr-2 text-left font-medium transition-colors ${
                depth === 0 ? 'text-base text-gray-900' : 'text-sm text-gray-700'
              } hover:text-primary-600`}
              onClick={() => onToggle(item.id)}
              aria-expanded={isExpanded}
            >
              {item.label}
            </button>
            <button
              type="button"
              className="p-3 text-gray-400 hover:text-primary-600 transition-colors"
              onClick={() => onToggle(item.id)}
              aria-label={isExpanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
            >
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </>
        ) : (
          <button
            type="button"
            className={`flex w-full items-center gap-2 py-3.5 pr-4 text-left transition-colors ${
              depth === 0 ? 'text-base font-medium text-gray-900' : 'text-sm text-gray-700'
            } hover:text-primary-600`}
            onClick={() => onNavigate(item)}
          >
            <span className="flex-1">{item.label}</span>
            {item.link_type === 'custom_url' &&
              item.link_target &&
              isExternalUrl(item.link_target) && (
                <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
              )}
          </button>
        )}
      </div>

      {/* Children accordion with animation */}
      {hasChildren && isExpanded && (
        <div
          className="bg-gray-50/50 animate-fade-in-up"
          role="region"
          aria-label={`${item.label} submenu`}
        >
          {item.children.map((child) => (
            <AccordionItem
              key={child.id}
              item={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── MobileMenu Component ─────────────────────────────────────────────────────

export function MobileMenu({ slug = 'main-menu', menu: menuProp }: MobileMenuProps) {
  const { menu: fetchedMenu } = usePublicMenu(slug)
  const menu = menuProp ?? fetchedMenu

  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const openMenu = useCallback(() => setIsOpen(true), [])
  const closeMenu = useCallback(() => {
    setIsOpen(false)
    setExpandedIds(new Set())
  }, [])

  const handleToggleItem = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleNavigate = useCallback(
    (item: MenuItem) => {
      const href = getItemHref(item)

      if (item.link_type === 'custom_url' && item.link_target && isExternalUrl(item.link_target)) {
        window.open(href, '_blank', 'noopener,noreferrer')
      } else {
        navigate(href)
      }

      closeMenu()
    },
    [navigate, closeMenu],
  )

  return (
    <div className="lg:hidden">
      {/* Hamburger toggle button */}
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-full p-2 text-gray-600 border border-gray-100 bg-gray-50/50 hover:bg-primary-50/50 hover:border-primary-100/50 hover:text-primary-600 transition-all hover:scale-105 shadow-sm sm:p-3"
        onClick={openMenu}
        aria-expanded={isOpen}
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
              onClick={closeMenu}
              aria-hidden="true"
            />

            <div
              className="fixed top-0 right-0 z-[60] flex h-[100dvh] w-[min(85vw,20rem)] flex-col bg-white shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <span className="text-lg font-heading font-bold text-gray-900">Menu</span>
                <button
                  type="button"
                  className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  onClick={closeMenu}
                  aria-label="Close navigation menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Drawer content */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {menu && menu.items && menu.items.length > 0 ? (
                  <nav aria-label="Mobile navigation">
                    {menu.items.map((item) => (
                      <AccordionItem
                        key={item.id}
                        item={item}
                        depth={0}
                        expandedIds={expandedIds}
                        onToggle={handleToggleItem}
                        onNavigate={handleNavigate}
                      />
                    ))}
                  </nav>
                ) : (
                  <nav className="py-2" aria-label="Mobile navigation">
                    <Link to="/" className="flex items-center gap-3 px-5 py-3.5 text-base font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 transition-colors" onClick={closeMenu}>
                      <Home className="h-5 w-5 text-gray-400" />
                      Home
                    </Link>
                    <Link to="/products" className="flex items-center gap-3 px-5 py-3.5 text-base font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 transition-colors" onClick={closeMenu}>
                      <ShoppingBag className="h-5 w-5 text-gray-400" />
                      Products
                    </Link>
                    <Link to="/contact" className="flex items-center gap-3 px-5 py-3.5 text-base font-medium text-gray-900 hover:text-primary-600 hover:bg-gray-50 transition-colors" onClick={closeMenu}>
                      <Phone className="h-5 w-5 text-gray-400" />
                      Contact
                    </Link>
                  </nav>
                )}
              </div>

              {/* Drawer footer */}
              <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                <p className="text-xs text-gray-400 text-center">© {new Date().getFullYear()} Arts & Handicrafts</p>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  )
}

export default MobileMenu
